import {
  CREDIT_KARMA_LEAD_URL,
  CREDIT_KARMA_SUBSCRIPTION_KEY,
  CreditKarmaLeadResponse,
  DEFAULT_CREDIT_KARMA_VEHICLE_ID,
  buildCreditKarmaEmail,
  buildCreditKarmaPayload
} from './creditKarmaLead';

export type PartnerId = 'credit-karma';

export type PartnerOption = {
  id: PartnerId;
  label: string;
};

// Alphabetized by label; add new partners here as they come online.
export const PARTNER_OPTIONS: PartnerOption[] = [
  { id: 'credit-karma', label: 'Credit Karma' }
];

type CreditKarmaLeadResult = {
  continueUrl: string;
  email: string;
  firstName: string;
};

// Local dev hosts where the Next.js dev server serves /tools/test/* API routes
// (localhost, localhost:3000, localhost.*, local.*). On testazure/prod those
// routes 404 (feature branches are JS-only deploys), so the Partners tooling is
// hidden there and the direct apim call is used as the fallback path.
export function isLocalHost(): boolean {
  const { hostname } = window.location;

  return (
    hostname === 'localhost' ||
    hostname.startsWith('localhost.') ||
    hostname.startsWith('local.')
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function createCreditKarmaLeadViaProxy(options: {
  email?: string;
  firstName?: string;
  vehicleId?: string;
}): Promise<CreditKarmaLeadResult> {
  const response = await fetch('/tools/test/credit-karma-lead', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      email: options.email,
      firstName: options.firstName,
      vehicleId: options.vehicleId
    })
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(
      body?.error || `Credit Karma lead failed: ${response.statusText}`
    );
  }

  return (await response.json()) as CreditKarmaLeadResult;
}

async function createCreditKarmaLeadDirect(options: {
  email?: string;
  firstName?: string;
  vehicleId?: string;
}): Promise<CreditKarmaLeadResult> {
  const firstName = options.firstName || 'NICHOLE';
  const email = options.email || buildCreditKarmaEmail(firstName);
  const vehicleId = options.vehicleId || DEFAULT_CREDIT_KARMA_VEHICLE_ID;
  const payload = buildCreditKarmaPayload(email, firstName, vehicleId);

  const response = await fetch(CREDIT_KARMA_LEAD_URL, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'Ocp-Apim-Subscription-Key': CREDIT_KARMA_SUBSCRIPTION_KEY
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Credit Karma lead failed: ${response.status} ${response.statusText} — ${body}`
    );
  }

  const data = (await response.json()) as CreditKarmaLeadResponse;
  const continueUrl = data.C?.continueUrl;
  if (!continueUrl) {
    throw new Error('Credit Karma response missing continueUrl');
  }

  return { continueUrl, email, firstName };
}

export async function createCreditKarmaLead(
  options: {
    email?: string;
    firstName?: string;
    vehicleId?: string;
  } = {}
): Promise<CreditKarmaLeadResult> {
  const run = isLocalHost()
    ? createCreditKarmaLeadViaProxy
    : createCreditKarmaLeadDirect;

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      return await run(options);
    } catch (error) {
      if (attempt === 3) throw error;
      await sleep(Math.pow(2, attempt - 1) * 1000);
    }
  }

  throw new Error('Failed to generate Credit Karma lead after 3 attempts');
}
